import { DEFAULT_OPERATOR } from './filterConstants';
import type { Filter } from './types';

export function parseOperator(operatorText: string): string {
  const normalized = operatorText.toLowerCase().trim();

  switch (true) {
    case normalized === 'does not contain' ||
      normalized.includes('does not contain'):
    case normalized === 'not contain' || normalized.includes('not contain'):
      return 'not_contains';
    case normalized === 'does not equal' ||
      normalized.includes('does not equal'):
    case normalized === 'not equal' || normalized.includes('not equal'):
    case normalized === 'is not' || normalized.includes('is not'):
      return 'not_equals';
    case normalized === 'greater than' || normalized.includes('greater than'):
      return 'greater_than';
    case normalized === 'less than' || normalized.includes('less than'):
      return 'less_than';
    case normalized === 'equals' || normalized === 'equal':
    case normalized === 'is':
      return 'equals';
    case normalized === 'contains' || normalized === 'contain':
      return 'contains';
    default:
      return DEFAULT_OPERATOR;
  }
}

// Parses filter input in comma-separated format: "label, operator value"
function parseCommaSeparatedFormat(input: string): Filter | null {
  const parts = input
    .trim()
    .split(',')
    .map(s => s.trim());

  if (parts.length < 2) return null;

  const label = parts[0];
  const rest = parts.slice(1).join(',').trim();

  const operatorMatch = rest.match(
    /^(does not contain|does not equal|not contains?|not equals?|is not|contains?|equals?|greater than|less than|is)\s+(.+)$/i,
  );

  if (operatorMatch) {
    const operatorText = operatorMatch[1];
    const value = operatorMatch[2];
    const operator = parseOperator(operatorText);

    return {
      id: crypto.randomUUID(),
      label: normalizeLabel(label),
      operator,
      value,
    };
  }

  return {
    id: crypto.randomUUID(),
    label: normalizeLabel(label),
    operator: DEFAULT_OPERATOR,
    value: rest,
  };
}

// Parses filter input in space-separated format: "label operator value"
function parseSpaceSeparatedFormat(input: string): Filter | null {
  const words = input.trim().split(/\s+/);

  if (words.length < 3) return null;

  const label = words[0];
  let operatorText = '';
  let valueStartIndex = 1;

  if (
    words.length >= 4 &&
    words[1].toLowerCase() === 'does' &&
    words[2].toLowerCase() === 'not'
  ) {
    operatorText = `${words[1]} ${words[2]} ${words[3]}`.toLowerCase();
    valueStartIndex = 4;
  } else if (
    words.length >= 3 &&
    words[1].toLowerCase() === 'is' &&
    words[2].toLowerCase() === 'not'
  ) {
    operatorText = `${words[1]} ${words[2]}`.toLowerCase();
    valueStartIndex = 3;
  } else if (
    words.length >= 3 &&
    (words[1].toLowerCase() === 'greater' || words[1].toLowerCase() === 'less')
  ) {
    operatorText = `${words[1]} ${words[2]}`.toLowerCase();
    valueStartIndex = 3;
  } else {
    operatorText = words[1].toLowerCase();
    valueStartIndex = 2;
  }

  const value = words.slice(valueStartIndex).join(' ');
  const operator = parseOperator(operatorText);

  return {
    id: crypto.randomUUID(),
    label: normalizeLabel(label),
    operator,
    value,
  };
}

function normalizeLabel(label: string): string {
  return label.toLowerCase().replace(/\s+/g, '_');
}

// Parses filter input string and returns a Filter object
export function parseFilterInput(input: string): Filter | null {
  if (!input.trim()) return null;

  const commaFormatResult = parseCommaSeparatedFormat(input);
  if (commaFormatResult) return commaFormatResult;

  const spaceFormatResult = parseSpaceSeparatedFormat(input);
  if (spaceFormatResult) return spaceFormatResult;

  return null;
}

export function createFilterFromLabel(label: string): Filter {
  return {
    id: crypto.randomUUID(),
    label,
    operator: DEFAULT_OPERATOR,
    value: '',
  };
}
